import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ContentProvider } from "@/context/ContentContext";
import AccessibilityWidget from "@/components/AccessibilityWidget";
import CookieConsent from "@/components/CookieConsent";

// Landing resta eager (è la route d'ingresso, deve renderizzare subito per l'LCP).
import Landing from "@/pages/Landing";

// Tutte le altre pagine sono caricate SOLO quando servono (code-splitting):
// così la home non scarica il codice di CRM, blog, prenotazioni, firma, ecc.
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Privacy = lazy(() => import("@/pages/legal/Privacy"));
const Termini = lazy(() => import("@/pages/legal/Termini"));
const CookiePolicy = lazy(() => import("@/pages/legal/Cookie"));
const Login = lazy(() => import("@/pages/admin/Login"));
const Register = lazy(() => import("@/pages/admin/Register"));
const OAuthCallback = lazy(() => import("@/pages/admin/OAuthCallback"));
const Crm = lazy(() => import("@/pages/admin/Crm"));
const GoogleCalendarCallback = lazy(() => import("@/pages/admin/GoogleCalendarCallback"));
const SignDocument = lazy(() => import("@/pages/SignDocument"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const ClientArea = lazy(() => import("@/pages/ClientArea"));

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--ink-950)]">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-500/30 border-t-emerald-500" />
    </div>
  );
}

// Global chrome (cookie banner + a11y widget) hidden inside the authenticated CRM
function ChromeWidgets() {
  const { pathname } = useLocation();
  const hidden = ["/crm", "/firma", "/prenota", "/pagamento", "/area-clienti", "/google-calendar"];
  if (hidden.some((p) => pathname.startsWith(p))) return null;
  return (
    <>
      <AccessibilityWidget />
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <div className="App relative">
      <ContentProvider>
        <BrowserRouter>
          <a
            href="#main"
            className="sr-only left-4 top-4 z-[300] rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white focus:not-sr-only focus:absolute focus:outline-none focus:ring-2 focus:ring-emerald-300"
          >
            Salta al contenuto
          </a>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/termini" element={<Termini />} />
              <Route path="/cookie" element={<CookiePolicy />} />
              <Route path="/admin" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/:provider" element={<OAuthCallback />} />
              <Route path="/crm" element={<Crm />} />
              <Route path="/google-calendar/callback" element={<GoogleCalendarCallback />} />
              <Route path="/firma/preventivo/:token" element={<SignDocument kind="quote" />} />
              <Route path="/firma/contratto/:token" element={<SignDocument kind="contract" />} />
              <Route path="/prenota" element={<BookingPage />} />
              <Route path="/pagamento/success" element={<PaymentSuccess />} />
              <Route path="/area-clienti" element={<ClientArea />} />
            </Routes>
          </Suspense>
          <ChromeWidgets />
        </BrowserRouter>
        <Toaster position="top-center" richColors theme="light" />
      </ContentProvider>
    </div>
  );
}

export default App;
